import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Sparkles,
  ArrowLeft,
  Coffee,
  TrendingUp,
  AlertTriangle,
  Search,
  Send,
  Check,
  Boxes,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getDemoHeadline, getSummary, getInventoryKpis, track } from "../lib/api";
import CountUp from "./CountUp";

const STORAGE_KEY = "optygo_morning_brief_dismissed_on";
const SESSION_KEY = "optygo_morning_brief_seen_session";

/**
 * Morning briefing overlay — shows once per day on first visit AND only once per
 * browser session. Even if a stale render re-mounts this component, the session
 * flag prevents re-appearance.
 */
export default function MorningBriefing() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [headline, setHeadline] = useState(null);
  const [summary, setSummary] = useState(null);
  const [invKpis, setInvKpis] = useState(null);
  const [shared, setShared] = useState(false);
  const user = (() => {
    try { return JSON.parse(localStorage.getItem("pulse_user") || "{}"); } catch { return {}; }
  })();

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const dismissedOn = localStorage.getItem(STORAGE_KEY);
    const seenInSession = sessionStorage.getItem(SESSION_KEY);
    if (dismissedOn === today) return; // already seen today
    if (seenInSession) return; // already seen this browser session

    // Fetch data first, only show if backend responds
    Promise.all([getDemoHeadline(), getSummary(), getInventoryKpis().catch(() => null)])
      .then(([hl, sm, ik]) => {
        setHeadline(hl.data);
        setSummary(sm.data);
        setInvKpis(ik?.data || null);
        setVisible(true);
        sessionStorage.setItem(SESSION_KEY, "1");
        track("morning_brief_show", "/", {});
      })
      .catch(() => {
        // silent — just don't show briefing on backend failure
      });
  }, []);

  const dismiss = (reason) => {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem(STORAGE_KEY, today);
    sessionStorage.setItem(SESSION_KEY, "1");
    track("morning_brief_dismiss", "/", { reason });
    setVisible(false);
  };

  const openItem = (tab, kind) => {
    track("morning_brief_cta", "/", { tab, kind });
    dismiss("cta");
    navigate(`/actions?tab=${tab}`);
  };

  const openInventory = () => {
    track("morning_brief_cta", "/", { tab: "inventory", kind: "inventory" });
    dismiss("cta");
    navigate("/inventory");
  };

  const shareWhatsapp = () => {
    if (!headline) return;
    const invLine = invKpis?.available
      ? `📦 ${invKpis.critical_restock_count} פריטים דחופים להזמין · ₪${formatShort(invKpis.tied_up_capital)} הון במלאי\n`
      : "";
    const summaryText =
      `📊 Optygo Pulse · בריפינג בוקר\n\n` +
      `המחזור החודשי: ₪${formatShort(headline.current_month_revenue || 0)} (${headline.trend_pct > 0 ? "+" : ""}${headline.trend_pct}% מול ${headline.previous_month_name})\n\n` +
      `🟢 ${headline.opportunities} פריטים בקצב גידול חד\n` +
      `🟡 ${headline.risks} פערי מחירים שיכולים לתקן רווחיות\n` +
      `🔴 ${headline.anomalies} חשד למחיר/עלות שדורש בדיקה\n` +
      invLine +
      `\nנתח עכשיו ב-Optygo Pulse →`;
    const url = `https://wa.me/?text=${encodeURIComponent(summaryText)}`;
    window.open(url, "_blank");
    track("morning_brief_whatsapp", "/", {});
    setShared(true);
  };

  if (!visible || !headline || !summary) return null;

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "בוקר טוב" : h < 17 ? "צהריים טובים" : "ערב טוב";
  })();

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="morning-briefing-overlay"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/75 backdrop-blur-xl"
            onClick={() => dismiss("backdrop")}
          />

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl bg-card border border-border rounded-lg shadow-2xl overflow-hidden"
            data-testid="morning-briefing-card"
          >
            {/* Subtle ambient glow */}
            <div className="absolute -top-32 -right-32 w-80 h-80 bg-primary/8 rounded-full blur-3xl pointer-events-none" />

            {/* Header */}
            <div className="relative p-6 pb-4 border-b border-border/50">
              <button
                onClick={() => dismiss("close")}
                className="absolute top-4 left-4 text-zinc-500 hover:text-foreground transition-colors p-1.5"
                data-testid="morning-brief-close"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="mb-3" data-testid="morning-brief-brand">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                  Optygo Pulse · בריפינג בוקר
                </div>
                <h1 className="text-xl font-bold tracking-tight mt-0.5 flex items-center gap-2">
                  {greeting}, {user.name || "אסף"} <Coffee className="w-4 h-4 text-primary/70" />
                </h1>
              </div>

              <p className="text-xs text-zinc-400 leading-relaxed">
                הבוקר Optygo Pulse סרק{" "}
                <span className="font-mono text-foreground">{(summary.rows || 0).toLocaleString()}</span>{" "}
                שורות מ-Priority{invKpis?.available ? <> ו-<span className="font-mono text-foreground">{invKpis.total_skus.toLocaleString()}</span> מק״טים במלאי</> : null}.
                הנה הדברים שחשוב שתדע:
              </p>
            </div>

            {/* Bullets */}
            <div className="relative p-6 space-y-2.5">
              <BriefItem
                index={1}
                color="opportunity"
                icon={TrendingUp}
                label="הזדמנויות"
                count={headline.opportunities}
                description="פריטים בקצב גידול חד (40%+ ב-30 ימים)"
                onClick={() => openItem("growers", "opportunity")}
              />
              <BriefItem
                index={2}
                color="watch"
                icon={AlertTriangle}
                label="פערי מחירים"
                count={headline.risks}
                description="לקוחות שילמו שונה על אותו פריט — פוטנציאל רווחיות"
                onClick={() => openItem("price_variance", "watch")}
              />
              <BriefItem
                index={3}
                color="critical"
                icon={Search}
                label="חשד למחיר/עלות"
                count={headline.anomalies}
                description="מחיר מכירה נמוך משמעותית מהעלות — בדיקה ידנית"
                onClick={() => openItem("cost_anomalies", "critical")}
              />
              {invKpis?.available && (
                <BriefItem
                  index={4}
                  color="critical"
                  icon={Boxes}
                  label="להזמין דחוף ממלאי"
                  count={invKpis.critical_restock_count}
                  description={`${invKpis.stockouts_count} פריטים חסרים · ₪${formatShort(invKpis.tied_up_capital)} הון במלאי`}
                  onClick={openInventory}
                />
              )}
            </div>

            {/* CTA */}
            <div className="relative p-6 pt-2 flex flex-col sm:flex-row gap-2 border-t border-border/40">
              <button
                onClick={() => dismiss("open_dashboard")}
                className="flex-1 px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-semibold flex items-center justify-center gap-2"
                data-testid="morning-brief-open-dashboard"
              >
                פתח את הדשבורד <ArrowLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={shareWhatsapp}
                disabled={shared}
                className={`flex-1 px-4 py-2.5 rounded-md border border-border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  shared
                    ? "bg-severity-opportunity/10 text-severity-opportunity border-severity-opportunity/40"
                    : "text-foreground hover:border-primary/40 hover:bg-primary/5"
                }`}
                data-testid="morning-brief-whatsapp"
              >
                {shared ? (
                  <><Check className="w-4 h-4" /> נשלח</>
                ) : (
                  <><Send className="w-3.5 h-3.5" /> שלח ל-WhatsApp</>
                )}
              </button>
            </div>

            {/* Footer note */}
            <div className="relative px-6 pb-4 flex items-center justify-between text-[10px] text-zinc-600 font-mono">
              <span>הבריפינג הזה יוצג פעם ביום</span>
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-2.5 h-2.5 text-primary/60" />
                AI · {new Date().toLocaleDateString("he-IL", { day: "numeric", month: "long" })}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function BriefItem({ index, color, icon: Icon, label, count, description, onClick }) {
  const styles = {
    opportunity: { ring: "border-severity-opportunity/30", text: "text-severity-opportunity", bg: "bg-severity-opportunity/10" },
    watch: { ring: "border-severity-watch/30", text: "text-severity-watch", bg: "bg-severity-watch/10" },
    critical: { ring: "border-severity-critical/30", text: "text-severity-critical", bg: "bg-severity-critical/10" },
  }[color];

  return (
    <button
      onClick={onClick}
      className={`group w-full text-right flex items-center gap-3 p-3 rounded-md border border-border bg-[#0A0A0E]/40 hover:${styles.ring} hover:bg-[#0A0A0E]/80 transition-all`}
      data-testid={`morning-brief-item-${index}`}
    >
      <div className={`w-10 h-10 rounded-md ${styles.bg} flex items-center justify-center shrink-0`}>
        <Icon className={`w-4 h-4 ${styles.text}`} strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <CountUp value={count} duration={900} className={`text-2xl font-mono font-semibold ${styles.text} leading-none`} />
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
        <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{description}</div>
      </div>
      <ArrowLeft className={`w-4 h-4 text-zinc-600 group-hover:${styles.text} transition-colors shrink-0`} />
    </button>
  );
}

function formatShort(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return Math.round(n).toLocaleString();
}
